import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'
import { EmptyChart } from './EmptyChart'
import { useTargetTitles } from '../hooks/useTargetTitles'
import { useState, useEffect } from 'react'

// Responsive hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

export function TargetTitlesChart({ className }: { className?: string }) {
  const { data, isLoading, error } = useTargetTitles()
  const isMobile = useIsMobile()

  if (isLoading) return <ChartSkeleton className={className} />
  if (error) return <ChartError className={className} />
  if (!data?.length) return <EmptyChart message="No target title data available" className={className} />

  const colors = [
    'hsl(var(--chart-1))', // Primary teal
    'hsl(var(--chart-2))', // Primary coral
    'hsl(var(--chart-3))', // Darker teal
    'hsl(var(--chart-4))', // Darker coral
    'hsl(var(--chart-5))', // Medium teal
    'hsl(var(--chart-6))', // Light coral
    'hsl(var(--chart-7))', // Very dark teal
    'hsl(var(--chart-1))', // Repeat pattern for more than 7 items
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
  ]

  // Map data to include colors
  const chartData = data.map((item, idx) => ({
    ...item,
    fill: colors[idx % colors.length]
  }))

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-xl font-semibold">Top 10 Target Titles</h3>
        <p className="text-base text-muted-foreground">Most common target titles for current year</p>
      </div>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 10, left: 5, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              type="number" 
              className="text-xs"
              tickFormatter={(value) => Math.round(value).toString()}
              domain={[0, 'dataMax']}
              allowDecimals={false}
            />
            <YAxis 
              dataKey="title" 
              type="category" 
              className="text-xs"
              width={isMobile ? 0 : 200}
              tick={isMobile ? false : true}
              axisLine={isMobile ? false : true}
              tickLine={isMobile ? false : true}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0];
                  return (
                    <div className="rounded-lg border bg-background p-2 shadow-md">
                      <div className="flex items-center gap-2">
                        <div 
                          className="h-2 w-2 rounded-sm" 
                          style={{ backgroundColor: data.payload.fill }}
                        />
                        <span className="text-xs">
                          {data.payload.title}
                        </span>
                        <span className="text-xs font-semibold ml-1">
                          {data.value}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="count" barSize={28}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Mobile Legend - Show when bars are too short to interact with */}
      {isMobile && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Target Titles</h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            {chartData.map((entry) => (
              <div key={entry.title} className="flex items-center justify-between p-2 rounded bg-muted/30">
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: entry.fill }}
                  />
                  <span className="text-foreground truncate">{entry.title}</span>
                </div>
                <span className="font-medium text-foreground ml-2 flex-shrink-0">
                  {entry.count.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
} 